import { normalizeBookmarkedPages, migrateWorkflowBookmarkHref, isRetiredBookmark } from '../lib/bookmarks';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ModalState {
  isOpen: boolean;
  data?: unknown;
}

interface UIStoreState {
  // Sidebar state
  sidebarOpen: boolean;

  // Modal management
  modals: Record<string, ModalState>;

  // Toast notifications are handled by react-hot-toast
  // This just tracks notification preferences
  notificationPreferences: {
    showSyncNotifications: boolean;
    showAlertNotifications: boolean;
    playSound: boolean;
  };

  // Table preferences per table (persisted)
  tablePreferences: Record<
    string,
    {
      columnVisibility?: Record<string, boolean>;
      columnOrder?: string[];
      columnSizing?: Record<string, number>;
      sorting?: { id: string; desc: boolean }[];
      density?: 'compact' | 'normal' | 'comfortable';
    }
  >;

  // Bookmarked pages
  bookmarkedPages: string[];

  // Command palette
  commandPaletteOpen: boolean;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Bookmarks
  setBookmarkedPages: (hrefs: string[]) => void;
  toggleBookmark: (href: string) => void;
  removeBookmark: (href: string) => void;
  isBookmarked: (href: string) => boolean;

  openModal: (modalId: string, data?: unknown) => void;
  closeModal: (modalId: string) => void;
  getModalState: (modalId: string) => ModalState;

  setNotificationPreferences: (
    prefs: Partial<UIStoreState['notificationPreferences']>
  ) => void;

  setTablePreferences: (
    tableId: string,
    prefs: Partial<UIStoreState['tablePreferences'][string]>
  ) => void;
  getTablePreferences: (tableId: string) => UIStoreState['tablePreferences'][string];

  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;

  reset: () => void;
}

const initialState = {
  sidebarOpen: true,
  modals: {},
  notificationPreferences: {
    showSyncNotifications: true,
    showAlertNotifications: true,
    playSound: false,
  },
  tablePreferences: {},
  bookmarkedPages: [] as string[],
  commandPaletteOpen: false,
};

export const useUIStore = create<UIStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      setBookmarkedPages: (hrefs: string[]) => {
        set({ bookmarkedPages: normalizeBookmarkedPages(hrefs) });
      },

      toggleBookmark: (href: string) => {
        set((state) => {
          const normalizedHref = migrateWorkflowBookmarkHref(href.trim());
          if (
            isRetiredBookmark(normalizedHref)
            || !normalizedHref.startsWith('/')
            || normalizedHref.startsWith('//')
            || /\s/.test(normalizedHref)
          ) {
            return {};
          }

          const exists = state.bookmarkedPages.includes(normalizedHref);
          return {
            bookmarkedPages: exists
              ? state.bookmarkedPages.filter((p) => p !== normalizedHref)
              : [...state.bookmarkedPages, normalizedHref],
          };
        });
      },

      removeBookmark: (href: string) => {
        const normalizedHref = migrateWorkflowBookmarkHref(href.trim());
        set((state) => ({ bookmarkedPages: state.bookmarkedPages.filter((page) => page !== normalizedHref) }));
      },

      isBookmarked: (href: string) => {
        return get().bookmarkedPages.includes(migrateWorkflowBookmarkHref(href.trim()));
      },

      openModal: (modalId: string, data?: unknown) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [modalId]: { isOpen: true, data },
          },
        }));
      },

      closeModal: (modalId: string) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [modalId]: { isOpen: false, data: undefined },
          },
        }));
      },

      getModalState: (modalId: string) => {
        const state = get();
        return state.modals[modalId] || { isOpen: false };
      },

      setNotificationPreferences: (prefs) => {
        set((state) => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            ...prefs,
          },
        }));
      },

      setTablePreferences: (tableId: string, prefs) => {
        set((state) => ({
          tablePreferences: {
            ...state.tablePreferences,
            [tableId]: {
              ...state.tablePreferences[tableId],
              ...prefs,
            },
          },
        }));
      },

      getTablePreferences: (tableId: string) => {
        const state = get();
        return state.tablePreferences[tableId] || {};
      },

      setCommandPaletteOpen: (open: boolean) => {
        set({ commandPaletteOpen: open });
      },

      toggleCommandPalette: () => {
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen }));
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'zeroth-ui-state',
      version: 4,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState) => {
        const persisted = persistedState as Partial<UIStoreState> | undefined;
        return {
          ...persisted,
          bookmarkedPages: normalizeBookmarkedPages(persisted?.bookmarkedPages ?? []),
        } as UIStoreState;
      },
      partialize: (state) => ({
        notificationPreferences: state.notificationPreferences,
        tablePreferences: state.tablePreferences,
        bookmarkedPages: state.bookmarkedPages,
      }),
    }
  )
);

// Selector hooks
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useModalState = (modalId: string) =>
  useUIStore((state) => state.modals[modalId] || { isOpen: false });
export const useCommandPaletteOpen = () => useUIStore((state) => state.commandPaletteOpen);

// Modal IDs for type safety
export const MODAL_IDS = {
  PRODUCT_EDIT: 'product-edit',
  PRODUCT_IMPORT: 'product-import',
  SUPPLIER_FORM: 'supplier-form',
  SHIPMENT_CREATE: 'shipment-create',
  PO_CREATE: 'po-create',
  STORE_SETTINGS: 'store-settings',
  SYNC_DETAILS: 'sync-details',
  CONFIRM_DELETE: 'confirm-delete',
} as const;

export type ModalId = (typeof MODAL_IDS)[keyof typeof MODAL_IDS];
